import { useModal } from "@/Contexts";
import { PdfPreviewIcon } from "@/Icons/v2/PdfPreview";
import { Button } from "@/components/button";
import { CLIPPED_BUTTON_STYLE } from "@/constants/general";
import { useTrans } from "@/translations";

import React, { useCallback } from "react";

import html2pdf from "html2pdf.js";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { create } from "zustand";

type UsePdfPreview = {
  pdfPreviewOpen: boolean;
  setPdfPreviewOpen: (value: boolean) => void;
};

export const usePdfPreview = create<UsePdfPreview>((set) => ({
  pdfPreviewOpen: false,
  setPdfPreviewOpen: (_pdfPreviewOpen) =>
    set(() => ({ pdfPreviewOpen: _pdfPreviewOpen })),
}));

type PreviewProps = {
  generatePdf: () => void;
  close: () => void;
  view: React.JSX.Element;
};

export const PdfPreview: React.FC<PreviewProps> = ({
  generatePdf,
  close,
  view,
}) => {
  const { t } = useTrans();
  return (
    <div className="bg-grey1 absolute top-0 right-0 bottom-0 left-0 z-20 w-full justify-center">
      <div className="relative m-auto my-4 flex max-w-[1650px] items-center justify-between px-4">
        <Button
          label="actions:close"
          className="float-right mx-4"
          onClick={close}
          color="secondary"
        />
        <h1 className="absolute left-1/2 -translate-x-1/2 transform text-2xl">
          {t("genericLabels:pdfPreview")}
        </h1>
        <Button
          label="actions:generatePdf"
          className="float-left mx-4"
          onClick={generatePdf}
        />
      </div>
      <div className="relative flex h-full w-full items-center justify-center overflow-scroll">
        <div
          className="bg-grey1 absolute top-0 max-h-[2750px] max-w-[1650px] px-10 pb-10"
          id="page-content"
        >
          <h1 className="align-center mb-4 w-full text-center text-2xl">
            Export {moment().format("YYYYMMDD")}
          </h1>
          <div className="light bg-grey1 justify-items-stretch p-8 pt-0 text-black">
            {view}
          </div>
        </div>
      </div>
    </div>
  );
};

type PreviewButtonProps = {
  exportFileNameKey: string;
  buttonTextKey: string;
  pdfView: React.JSX.Element;
};

export const PdfPreviewButton: React.FC<PreviewButtonProps> = ({
  exportFileNameKey,
  buttonTextKey,
  pdfView,
}) => {
  const { t } = useTrans();
  const { setPdfPreviewOpen } = usePdfPreview();
  const { showModal, hideModal } = useModal();

  const generatePdf = useCallback(async () => {
    const el = document.getElementById("page-content");

    if (el) {
      const margin = 10;
      const PDF_WIDTH = el.getBoundingClientRect().width;
      const PDF_HEIGHT = el.getBoundingClientRect().height;
      const opt = {
        filename: `${t(exportFileNameKey)}_${moment().format("YYYYMMDD")}.pdf`,
        image: { type: "jpeg" as any, quality: 0.98 },
        margin: 10,
        html2canvas: {
          width: PDF_WIDTH,
          height: PDF_HEIGHT,
          windowWidth: window.innerWidth,
          windowHeight: PDF_HEIGHT,
          letterRendering: true,
          useCORS: true,
          backgroundColor: "#F7F2F2",
        },
        jsPDF: {
          unit: "px",
          format: [PDF_WIDTH + margin * 2, PDF_HEIGHT + margin * 2] as [
            number,
            number,
          ],
          orientation: (PDF_WIDTH > PDF_HEIGHT
            ? "landscape"
            : "portrait") as any,
          hotfixes: ["px_scaling"],
        },
      };
      await html2pdf().set(opt).from(el).save();
    }
  }, [exportFileNameKey, t]);

  const onPreviewButtonClick = useCallback(() => {
    setPdfPreviewOpen(true);
    showModal(
      <PdfPreview
        generatePdf={generatePdf}
        close={() => {
          setPdfPreviewOpen(false);
          hideModal();
        }}
        view={pdfView}
      />,
    );
  }, [generatePdf, hideModal, setPdfPreviewOpen, showModal, pdfView]);

  return (
    <button
      onClick={onPreviewButtonClick}
      title="Preview"
      className={twMerge(
        "bg-lime flex cursor-pointer items-center gap-2 px-[22px] py-[11px] text-[14px] font-medium text-black",
        "max-[1200px]:h-[34px] max-[1200px]:px-[11px]",
        CLIPPED_BUTTON_STYLE,
      )}
    >
      <PdfPreviewIcon size={15} />
      <span className="max-[1200px]:hidden">{t(buttonTextKey)}</span>
    </button>
  );
};
